import React from 'react';
import {
  Truck, PackageCheck, Building2, ShieldCheck, User, Users,
  CheckCircle2, Loader2, Clock, AlertTriangle, ChevronRight,
  Lock,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

/**
 * PM Spec V3 13/05/2026 — PartySelector
 *
 * Composant qui affiche la liste des parties signataires possibles pour
 * un bon donné, avec leur état (signé / en cours / en attente).
 * Utilisé en mode "tablette présence" pour qu'un opérateur puisse passer
 * la tablette d'acteur en acteur.
 *
 * Props :
 *   noteType: 'loading' | 'unloading'
 *   parties: [{party, label, signed, signedAt, linkState, preFillName, allowed}]
 *   onSelect(party)
 *   currentSession: 'admin' | 'driver' | 'distributed' (restreint l'accès)
 */

const PARTY_META = {
  shipper: { Icon: User, label: 'Représentant départ', subtitle: 'Site de chargement' },
  consignee: { Icon: User, label: 'Représentant arrivée', subtitle: 'Site de livraison' },
  driver: { Icon: Truck, label: 'Chauffeur', subtitle: 'Baobab Connect' },
  baobab_operator: { Icon: ShieldCheck, label: 'Opérateur Baobab', subtitle: 'Validation à distance' },
  observer: { Icon: Users, label: 'Observateur', subtitle: 'Optionnel' },
};

const stateConfig = (signed, linkState) => {
  if (signed) return { bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-600 text-white', label: 'Signé', Icon: CheckCircle2 };
  if (linkState === 'opened') return { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-500 text-white', label: 'En cours', Icon: Loader2 };
  if (linkState === 'expired') return { bg: 'bg-red-50 border-red-200', badge: 'bg-red-500 text-white', label: 'Expiré', Icon: AlertTriangle };
  if (linkState === 'revoked') return { bg: 'bg-slate-50 border-slate-200', badge: 'bg-slate-400 text-white', label: 'Révoqué', Icon: AlertTriangle };
  return { bg: 'bg-white border-slate-200', badge: 'bg-slate-100 text-slate-700', label: 'En attente', Icon: Clock };
};

export const PartySelector = ({
  noteType, parties = [], onSelect,
  currentSession = 'distributed',
  testId = 'party-selector',
}) => {
  const isLoading = noteType === 'loading';
  const TitleIcon = isLoading ? Truck : PackageCheck;
  const titleLabel = isLoading ? 'Bon de chargement' : 'Bon de déchargement';

  return (
    <Card data-testid={testId} className="border-emerald-100">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
            <TitleIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-base font-semibold text-slate-900">{titleLabel}</div>
            <div className="text-xs text-slate-500">Sélectionnez la personne qui doit signer</div>
          </div>
        </div>

        <div className="grid gap-3" data-testid={`${testId}-grid`}>
          {parties.map((p) => {
            const meta = PARTY_META[p.party] || PARTY_META.observer;
            const cfg = stateConfig(p.signed, p.linkState);
            const Icon = meta.Icon;
            const StateIcon = cfg.Icon;
            const disabled = p.signed || !p.allowed;
            return (
              <button
                key={p.party}
                type="button"
                disabled={disabled}
                onClick={() => onSelect?.(p.party)}
                data-testid={`${testId}-${p.party}`}
                className={`w-full text-left rounded-xl border-2 p-4 transition-all ${cfg.bg} ${
                  disabled
                    ? 'opacity-70 cursor-not-allowed'
                    : 'hover:shadow-md hover:border-emerald-400 active:scale-[0.99] cursor-pointer'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                    p.signed ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-base truncate">
                      {meta.label}
                    </div>
                    {p.preFillName && (
                      <div className="text-sm text-slate-700 truncate">
                        {p.preFillName}
                      </div>
                    )}
                    <div className="text-xs text-slate-500 truncate">
                      {meta.subtitle}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge className={`${cfg.badge} border-0 text-xs px-2 py-0.5 flex items-center gap-1`}>
                      <StateIcon className="w-3 h-3" />
                      {cfg.label}
                    </Badge>
                    {!disabled && <ChevronRight className="w-5 h-5 text-emerald-600 mt-1" />}
                    {!p.allowed && !p.signed && (
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Restreint
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {currentSession === 'driver' && (
          <div className="text-xs text-slate-500 italic flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            Vous pouvez signer uniquement votre propre partie depuis votre tablette.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PartySelector;
